import type { exchangeRatePartHistory } from "@/api/breakdownExchangeRateService";
import { InformationModal } from "@/styles/globalstyles";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import CloseButton from "../buttons/CloseButton";
import { useGetBreakdownExchangeRatePartHistory } from "@/hooks/breakdownExchangeRateHooks";
import { useMemo, useState } from "react";

interface ExchangeRatePartHistoryModalProps {
  open: boolean;
  handleClose: () => void;
  partId: number | null;
  partNumber?: string;
}

type HistoryWithDiff = exchangeRatePartHistory & {
  id: string;
  diff: number;
};

const ExchangeRatePartHistoryModal = ({
  open,
  handleClose,
  partId,
  partNumber,
}: ExchangeRatePartHistoryModalProps) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const { data: historyData, isLoading } =
    useGetBreakdownExchangeRatePartHistory(partId || 0);

  // คำนวณ diff จากราคาปีก่อนหน้า
  const historyWithDiff: HistoryWithDiff[] = useMemo(() => {
    if (!historyData || historyData.length === 0) return [];

    const filteredData = historyData.filter(
      (item) => item.year === selectedYear,
    );

    const sorted = [...filteredData].sort((a, b) => {
      return b.quarter - a.quarter;
    });

    // คำนวณ diff โดยเปรียบเทียบกับ quarter ก่อนหน้า
    return sorted.map((item, index) => {
      let diff = 0;
      if (index < sorted.length - 1) {
        const previousPrice = sorted[index + 1].part_price;
        diff = item.part_price - previousPrice;
      }

      return {
        ...item,
        id: `${item.year}-${item.quarter}-${item.part_id}`,
        diff,
      };
    });
  }, [historyData, selectedYear]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const columns: GridColDef<HistoryWithDiff>[] = [
    {
      field: "year",
      headerName: "Year",
      align: "right",
      headerAlign: "right",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            height: "100%",
          }}
        >
          <Typography fontSize="0.9rem" align="right">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "quarter",
      headerName: "Quarter",
      align: "right",
      headerAlign: "right",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            height: "100%",
          }}
        >
          <Typography fontSize="0.9rem" align="right">
            Q{params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "part_price",
      headerName: "Part Price (THB)",
      align: "right",
      headerAlign: "right",
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            height: "100%",
          }}
        >
          <Typography fontSize="0.9rem" align="right">
            {params.value?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    },
    {
      field: "diff",
      headerName: "Difference",
      align: "right",
      headerAlign: "right",
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            height: "100%",
          }}
        >
          <Typography
            color={
              params.value === 0 ? "black" : params.value > 0 ? "red" : "green"
            }
            fontSize="0.9rem"
            align="right"
            fontWeight={params.value !== 0 ? 600 : 400}
          >
            {params.value > 0 ? "+" : ""}
            {params.value?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...InformationModal, maxWidth: "900px", maxHeight: "80vh" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2" mb={2}>
          Part Price History - {partNumber || `Part ID: ${partId}`}
        </Typography>

        <Box mb={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: "100%", height: "300px" }}>
          <DataGrid
            columns={columns}
            rows={historyWithDiff}
            loading={isLoading}
            disableRowSelectionOnClick
            disableColumnSelector
            disableColumnFilter
            disableColumnMenu
            hideFooterPagination
            hideFooter
            sx={{
              minHeight: "200px",
              "& .MuiDataGrid-cell": {
                fontSize: "0.9rem",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ExchangeRatePartHistoryModal;
